import { downloadBlob } from '@core/lib/downloadBlob'
import { useAppStore } from '@core/store'
import {
  buildBcfZip,
  type AttachedViewpoint,
  type VersaoBcf,
} from '@modules/bcf/lib/bcfBuilder'
import type { BcfTopic, ParsedBcf } from '@modules/bcf/lib/bcfParser'
import { latestParsed } from '@modules/bcf/store/bcfClientSlice'
import { classifyDimension } from '@viewer/lib/itemDimensions'
import { viewerApi } from '@viewer/lib/viewerApi'
import { viewpointsStore } from '@viewer/lib/viewpointsStore'
import type { Viewpoint, VpModelRef } from '@viewer/lib/viewpoint'
import type { ClashResult } from '@viewer/clash/lib'
import {
  ancoraDosConflitos,
  buildItemDrafts,
  caixaDeCorte,
  clashBcfFileName,
  clashViewpoint,
  extensoesDosConflitos,
  itensParaExportar,
  relacionadosDosRascunhos,
  utilizadoresDosTopicos,
  type ClashExportItem,
  type ClashGuidMap,
  type ClashTopicDraft,
} from './clashBcf'
import { clashKey, type ClashTriageMap } from './clashTriage'
import {
  comentarioDaMudanca,
  registoDaExportacao,
  relacionadosAnteriores,
  type ClashUltimaExportacao,
} from './clashExportacaoRegisto'
import {
  comentarioDosNovosNoGrupo,
  envioComNovos,
  relacoesDosNovos,
  type NovoTopico,
} from './clashModuloBcf'
import { guidsFor, namesFor } from './clashModels'
import { guidDoComentario, guidDoTopico, guidDoViewpoint } from './clashIds'
import { escolherReferencial, modelosGeoDaCena } from './clashReferencial'
import type { ClashEnvioModulo } from './clashReconciliacao'
import type { EspecialidadeDe, RegraDeExclusao } from './clashExclusao'
import { rascunhoDoModelador } from './clashTopicoModelador'
import {
  ASPECTO_IMAGEM,
  capturarImagens,
  mediaMs,
  palcoDoViewer,
  type CenaDeCaptura,
  type PalcoDeCaptura,
  type ProgressoDaCaptura,
} from './clashSnapshots'

// ---------------------------------------------------------------------------
// A parte suja da exportacao para BCF: resolver GlobalIds e pisos, tirar
// as imagens, criar topicos e escrever o ficheiro.
//
// Dois destinos, o mesmo conteudo:
//
//   MODULO BCF activo (workspace cloud com o modulo ligado): cria-se um
//   BCF novo no modulo, um topico por conflito (ou por grupo), e os
//   viewpoints, com a imagem, ficam no `viewpointsStore` (IndexedDB)
//   ligados por `viewpointRefs`. E o caminho que o painel do BCF ja usa
//   para exportar depois o `.bcfzip` com os viewpoints la dentro.
//
//   MODO LOCAL (sem modulo BCF): monta-se o mesmo `ParsedBcf` a mao e
//   escreve-se o `.bcfzip` para download com o `buildBcfZip`, com o
//   extensions.xml dos valores usados.
// ---------------------------------------------------------------------------

/** Resolve os GlobalId dos dois elementos de cada conflito, em lote por
 *  modelo (uma ida ao Fragments por modelo, nao por conflito). */
export async function resolveClashGuids(
  results: readonly ClashResult[],
): Promise<ClashGuidMap> {
  const wanted = new Map<string, Set<number>>()
  const want = (modelId: string, localId: number): void => {
    let set = wanted.get(modelId)
    if (!set) {
      set = new Set<number>()
      wanted.set(modelId, set)
    }
    set.add(localId)
  }
  for (const r of results) {
    want(r.pair.modelA, r.pair.localIdA)
    want(r.pair.modelB, r.pair.localIdB)
  }
  const byModel = new Map<string, Map<number, string | null>>()
  for (const [modelId, ids] of wanted) {
    const list = [...ids]
    const guids = await guidsFor(modelId, list)
    const map = new Map<number, string | null>()
    list.forEach((id, i) => map.set(id, guids[i] ?? null))
    byModel.set(modelId, map)
  }
  const out: ClashGuidMap = {}
  for (const r of results) {
    out[clashKey(r)] = [
      byModel.get(r.pair.modelA)?.get(r.pair.localIdA) ?? null,
      byModel.get(r.pair.modelB)?.get(r.pair.localIdB) ?? null,
    ]
  }
  return out
}

/** Nomes (Name do IFC) dos dois elementos de cada conflito, em lote por
 *  modelo, para o titulo dos topicos. Melhor esforco: sem nome, o titulo
 *  leva a classe. */
export async function resolveClashNomes(
  results: readonly ClashResult[],
): Promise<Record<string, [string | null, string | null]>> {
  const wanted = new Map<string, Set<number>>()
  for (const r of results) {
    for (const [m, id] of [[r.pair.modelA, r.pair.localIdA], [r.pair.modelB, r.pair.localIdB]] as const) {
      let set = wanted.get(m)
      if (!set) {
        set = new Set<number>()
        wanted.set(m, set)
      }
      set.add(id)
    }
  }
  const byModel = new Map<string, Map<number, string | null>>()
  for (const [modelId, ids] of wanted) {
    const list = [...ids]
    const nomes = await namesFor(modelId, list)
    byModel.set(modelId, new Map(list.map((id, i) => [id, nomes[i] ?? null])))
  }
  const out: Record<string, [string | null, string | null]> = {}
  for (const r of results) {
    out[clashKey(r)] = [
      byModel.get(r.pair.modelA)?.get(r.pair.localIdA) ?? null,
      byModel.get(r.pair.modelB)?.get(r.pair.localIdB) ?? null,
    ]
  }
  return out
}

/** Pisos dos dois elementos de cada conflito, quando se souberem. Um
 *  varrimento por modelo envolvido; melhor esforco (sem pisos, as
 *  etiquetas e a descricao seguem sem eles). */
export async function resolvePisos(
  results: readonly ClashResult[],
): Promise<Record<string, string[]>> {
  const modelos = new Set<string>()
  for (const r of results) {
    modelos.add(r.pair.modelA)
    modelos.add(r.pair.modelB)
  }
  const porModelo = new Map<string, Map<number, string>>()
  for (const id of modelos) {
    const model = viewerApi.getModel(id)
    if (!model) continue
    try {
      porModelo.set(id, await classifyDimension(model, { mode: 'storey' }))
    } catch {
      // Sem pisos neste modelo.
    }
  }
  const out: Record<string, string[]> = {}
  for (const r of results) {
    const a = porModelo.get(r.pair.modelA)?.get(r.pair.localIdA)
    const b = porModelo.get(r.pair.modelB)?.get(r.pair.localIdB)
    const pisos = [...new Set([a, b].filter((p): p is string => !!p))]
    if (pisos.length > 0) out[clashKey(r)] = pisos
  }
  return out
}

function modelRefs(): VpModelRef[] {
  return useAppStore
    .getState()
    .models.filter((m) => m.visible)
    .map((m) => ({
      filename: m.fileName,
      sha256: m.sha256,
      schema_version: m.schemaVersion ?? null,
    }))
}

/** Autor dos topicos quando nao ha sessao (modo local). */
export const AUTOR_SEM_SESSAO = 'VN2R openBIM Hub'

/** Exportacao interrompida por "Cancelar" durante as imagens. */
export class ExportacaoCancelada extends Error {
  constructor() {
    super('Exportação cancelada.')
    this.name = 'ExportacaoCancelada'
  }
}

export interface ClashBcfExportResult {
  /** 'modulo' criou tópicos no módulo BCF; 'ficheiro' descarregou um
   *  .bcfzip. */
  destination: 'modulo' | 'ficheiro'
  topics: number
  /** Nome do BCF criado ou do ficheiro descarregado. */
  name: string
  /** Imagens incluídas. */
  imagens: number
  /** Tempo médio por imagem, em ms (0 sem imagens). */
  msPorImagem: number
  /** Destino módulo: os tópicos criados, para a reconciliação das
   *  corridas seguintes os poder actualizar (depois de confirmada). */
  envio?: ClashEnvioModulo
  /** Destino ficheiro: o registo da última exportação depois desta (o
   *  anterior com o que agora saiu por cima). Quem chama grava-o. */
  registo?: ClashUltimaExportacao
}

export interface ClashBcfExportOptions {
  bcfModuleEnabled: boolean
  triage?: ClashTriageMap
  /** Grupos do agrupamento automatico (titulo e descricao do grupo, na
   *  descricao de cada topico do grupo). */
  grupos?: Record<string, { descricao?: string; titulo?: string }>
  author?: string | null
  projectName?: string
  /** Incluir uma imagem JPEG por tópico (por omissão, sim). */
  imagens?: boolean
  /** Juntar os conflitos do mesmo grupo num só tópico. Por omissão não:
   *  um tópico por conflito, com o grupo indicado (etiqueta, RelatedTopics
   *  e primeira linha da descrição). */
  umPorGrupo?: boolean
  versao?: VersaoBcf
  signal?: { aborted: boolean }
  onProgress?: (p: ProgressoDaCaptura) => void
  /** Palco das imagens. Por omissão o viewer; os testes passam um falso. */
  palco?: PalcoDeCaptura | null
  /** Modelo em cujas coordenadas sai o BCF. Sem ele, a regra de
   *  `escolherReferencial` (um modelo sem MapConversion, ou o CRS). */
  referencialModelo?: string | null
  /** Identificador do projecto (o da sessão): Guids estáveis do tópico,
   *  do viewpoint e dos comentários no ficheiro (ver clashIds.ts). */
  projecto?: string | null
  /** Data da exportação (ISO). Por omissão, agora. */
  agora?: string
  /** Registo da última exportação para ficheiro: o novo registo junta-se
   *  a ele. */
  ultimaExportacao?: ClashUltimaExportacao | null
  /** "Só o que mudou desde a última exportação": os conflitos dados são
   *  o delta (clashExportacaoRegisto.ts); os alterados levam o comentário
   *  da mudança e os RelatedTopics incluem os tópicos do mesmo grupo da
   *  exportação anterior. Precisa de `ultimaExportacao`. */
  soAlteracoes?: boolean
}

function alvos(item: ClashExportItem, lado: 'A' | 'B') {
  return item.results.map((r) =>
    lado === 'A'
      ? { modelId: r.pair.modelA, localId: r.pair.localIdA }
      : { modelId: r.pair.modelB, localId: r.pair.localIdB },
  )
}

/** O que a exportacao e o "Acrescentar os novos" preparam antes de
 *  escrever: os itens, os rascunhos, os viewpoints e as imagens. */
interface TopicosPreparados {
  itens: ClashExportItem[]
  drafts: ClashTopicDraft[]
  viewpoints: Viewpoint[]
  imagens: (Uint8Array | null)[]
  msPorImagem: number
  refs: VpModelRef[]
  referencial: AttachedViewpoint['referencial']
}

async function prepararTopicos(
  results: readonly ClashResult[],
  opts: ClashBcfExportOptions,
): Promise<TopicosPreparados> {
  const triage = opts.triage ?? {}
  const guids = await resolveClashGuids(results)
  const nomes = await resolveClashNomes(results)
  // Os GlobalId guardados na triagem (os da ultima corrida em que o
  // conflito ocorreu) mandam: um conflito resolvido que ja nao ocorre e
  // reconstruido com os localId antigos, que noutra revisao do IFC podem
  // ser de outro elemento. Nesse caso o nome lido pelo localId tambem nao
  // e dele, e o titulo fica com a classe.
  for (const r of results) {
    const k = clashKey(r)
    const g = triage[k]?.guids
    if (!g) continue
    const lidos = guids[k]
    if (lidos && (lidos[0] !== g[0] || lidos[1] !== g[1])) delete nomes[k]
    guids[k] = [g[0], g[1]]
  }
  const pisos = await resolvePisos(results)
  const refs = modelRefs()
  // As coordenadas do BCF (câmara, cortes e o ponto da descrição) saem
  // no referencial escolhido, nunca no do modelo activo.
  const referencial = escolherReferencial(modelosGeoDaCena(), opts.referencialModelo ?? null).referencial
  const itens = itensParaExportar(results, triage, opts.umPorGrupo === true)
  const drafts = buildItemDrafts(itens, { guids, nomes, triage, pisos, referencial, grupos: opts.grupos })

  const comImagens = opts.imagens !== false
  // Imagens sempre em 16:9 (1280 x 720), seja qual for o canvas; o
  // AspectRatio do viewpoint e o da imagem.
  const aspecto = comImagens ? ASPECTO_IMAGEM : undefined
  const viewpoints: Viewpoint[] = itens.map((item) =>
    clashViewpoint(item.results, {
      guidsA: item.results.map((r) => guids[clashKey(r)]?.[0] ?? null),
      guidsB: item.results.map((r) => guids[clashKey(r)]?.[1] ?? null),
      modelRefs: refs,
      aspect: aspecto,
    }),
  )

  // Imagens: a mesma camara e a mesma caixa do viewpoint.
  let imagens: (Uint8Array | null)[] = itens.map(() => null)
  let msPorImagem = 0
  if (comImagens) {
    const palco = opts.palco === undefined ? palcoDoViewer() : opts.palco
    if (palco) {
      const cenas: CenaDeCaptura[] = itens.map((item, i) => ({
        viewpoint: viewpoints[i],
        a: alvos(item, 'A'),
        b: alvos(item, 'B'),
        caixa: caixaDeCorte(item.results),
      }))
      const capt = await capturarImagens(cenas, palco, {
        signal: opts.signal,
        onProgress: opts.onProgress,
      })
      if (capt.cancelado) throw new ExportacaoCancelada()
      imagens = capt.imagens
      msPorImagem = mediaMs(capt.msPorImagem)
    }
  }
  return { itens, drafts, viewpoints, imagens, msPorImagem, refs, referencial }
}

/** Um topico criado no modulo BCF. */
interface TopicoCriado {
  /** Chave do item (clashKey ou "grupo:<id>"). */
  key: string
  guid: string
  /** As chaves dos conflitos do topico (num grupo, todos os pares do
   *  grupo na triagem). */
  chaves: string[]
}

/**
 * Cria os topicos preparados num BCF do modulo: o viewpoint (com a
 * imagem e a ancora do pin) no `viewpointsStore`, o topico com os Guids
 * escolhidos e os RelatedTopics, e os comentarios dos eventos.
 */
async function criarNoModulo(
  bcfLocalId: string,
  prep: TopicosPreparados,
  o: {
    guids: readonly string[]
    relacionados: readonly string[][]
    triage: ClashTriageMap
    author: string
    nowIso: string
    folder: string
  },
): Promise<TopicoCriado[]> {
  const store = useAppStore.getState()
  const criados: TopicoCriado[] = []
  for (let i = 0; i < prep.drafts.length; i++) {
    const draft = prep.drafts[i]
    const vpId = crypto.randomUUID()
    const png = prep.imagens[i]
    try {
      await viewpointsStore.save({
        id: vpId,
        name: draft.title,
        notes: '',
        createdAt: o.nowIso,
        updatedAt: o.nowIso,
        primaryHash: prep.refs[0]?.sha256 ?? '',
        modelHashes: prep.refs.map((m) => m.sha256),
        // A imagem vai como miniatura do viewpoint: e dela que o
        // modulo BCF tira o Snapshot ao exportar o .bcfzip.
        thumbnail: png ? new Blob([png.slice().buffer], { type: 'image/jpeg' }) : null,
        viewpoint: prep.viewpoints[i],
        state: {
          hidden: [],
          transparent: [],
          ghostMode: false,
          ghostOpacity: 0.12,
          transparencyOpacity: 0.2,
        },
        // O pin sobre o modelo: o ponto da interferencia (num grupo, o
        // centro dos pontos), no mundo three como o dos capturados.
        anchor: ancoraDosConflitos(prep.itens[i].results),
        folder: o.folder,
      })
    } catch (err) {
      console.warn('[clash] gravar viewpoint falhou', err)
    }
    const criado = store.bcfCreateTopic(
      bcfLocalId,
      {
        title: draft.title,
        description: draft.description,
        status: draft.status,
        priority: draft.priority,
        type: draft.type,
        assignedTo: draft.assignedTo,
        creationAuthor: o.author,
        creationDate: o.nowIso,
        modifiedAuthor: null,
        modifiedDate: null,
        dueDate: draft.dueDate,
        stage: null,
        labels: draft.labels,
        viewpointRefs: [vpId],
        guid: o.guids[i],
        ...(o.relacionados[i].length > 0 ? { relatedTopics: [...o.relacionados[i]] } : {}),
      },
      o.author,
    )
    if (!criado) continue
    // Num grupo, todos os pares do grupo na triagem (tambem os que ja
    // nao ocorrem): o topico so fecha com todos resolvidos.
    const grupo = draft.key.startsWith('grupo:') ? draft.key.slice('grupo:'.length) : null
    const doGrupo = grupo ? Object.keys(o.triage).filter((k) => o.triage[k].group === grupo) : []
    criados.push({ key: draft.key, guid: criado.guid, chaves: [...new Set([...draft.keys, ...doGrupo])] })
    // Os eventos das corridas ja havidos, como no ficheiro.
    for (const c of draft.comentarios) {
      useAppStore.getState().bcfAddComment(bcfLocalId, criado.guid, c.texto, c.autor?.trim() || o.author)
    }
  }
  const refreshed = useAppStore.getState().refreshViewpoints
  if (typeof refreshed === 'function') void refreshed()
  return criados
}

/**
 * Exporta os conflitos indicados. `bcfModuleEnabled` vem do chamador
 * (`useAuth`), para esta funcao continuar testavel sem contexto de
 * autenticacao.
 */
export async function exportClashesToBcf(
  results: readonly ClashResult[],
  opts: ClashBcfExportOptions,
): Promise<ClashBcfExportResult> {
  if (results.length === 0) throw new Error('Sem conflitos para exportar.')
  const triage = opts.triage ?? {}
  const prep = await prepararTopicos(results, opts)
  const { drafts } = prep
  const name = opts.projectName?.trim() || 'Conflitos'
  const author = opts.author?.trim() || AUTOR_SEM_SESSAO
  const nowIso = opts.agora ?? new Date().toISOString()
  const nImagens = prep.imagens.filter((x) => x !== null).length

  if (opts.bcfModuleEnabled) {
    const bcf = useAppStore.getState().bcfCreateEmpty(name)
    // Os Guids escolhem-se antes de criar, para cada topico ja nascer com
    // os RelatedTopics dos outros do mesmo grupo. Estaveis, como no
    // ficheiro: o mesmo conflito sai sempre com o mesmo Guid (dava um
    // aleatorio a cada "Criar BCF", e duas exportacoes do Cha139 no mesmo
    // dia nao partilhavam nenhum topico).
    const projecto = opts.projecto ?? null
    const guidsDosTopicos = drafts.map((d) => (projecto ? guidDoTopico(projecto, d.identidade) : crypto.randomUUID()))
    const criados = await criarNoModulo(bcf.localId, prep, {
      guids: guidsDosTopicos,
      relacionados: relacionadosDosRascunhos(drafts, guidsDosTopicos),
      triage,
      author,
      nowIso,
      folder: name,
    })
    const envio: ClashEnvioModulo = { bcfLocalId: bcf.localId, topicos: {}, aplicadoAte: nowIso }
    for (const c of criados) envio.topicos[c.key] = { guid: c.guid, chaves: c.chaves }
    return {
      destination: 'modulo',
      topics: drafts.length,
      name: bcf.name,
      imagens: nImagens,
      msPorImagem: prep.msPorImagem,
      envio,
    }
  }

  // Modo local: monta-se o BCF a mao e escreve-se o ficheiro.
  const anterior = opts.ultimaExportacao ?? null
  const { parsed, attachments } = montarBcfDosConflitos(drafts, prep.viewpoints, prep.imagens, {
    name,
    author,
    nowIso,
    referencial: prep.referencial,
    projecto: opts.projecto ?? null,
    // So o que mudou: os alterados levam o comentario da mudanca e os
    // RelatedTopics apontam tambem para os topicos do mesmo grupo das
    // exportacoes anteriores que nao vao neste ficheiro.
    ...(opts.soAlteracoes && anterior ? { anterior, triage } : {}),
  })
  const blob = await buildBcfZip(parsed, attachments, {
    versao: opts.versao ?? '2.1',
    utilizadores: utilizadoresDosTopicos(drafts),
  })
  const filename = clashBcfFileName()
  downloadBlob(blob, filename, 'application/zip')
  return {
    destination: 'ficheiro',
    topics: drafts.length,
    name: filename,
    imagens: nImagens,
    msPorImagem: prep.msPorImagem,
    registo: registoDaExportacao(
      anterior,
      drafts,
      parsed.topics.map((t) => t.guid),
      triage,
      nowIso,
    ),
  }
}

/**
 * "Criar tópico para o modelador" (Corrigir na origem): UM tópico com a
 * regra e a lista dos elementos dos conflitos que ela apanha, em vez de
 * um tópico por conflito. Mesmo destino da exportação: um BCF novo no
 * módulo, ou um .bcfzip para descarregar. O viewpoint selecciona e pinta
 * todos os elementos, sem caixa de corte (estão espalhados pelo edifício).
 */
export async function exportarTopicoDoModelador(
  results: readonly ClashResult[],
  regra: RegraDeExclusao,
  opts: {
    bcfModuleEnabled: boolean
    triage: ClashTriageMap
    especialidadeDe: EspecialidadeDe
    author?: string | null
    projecto?: string | null
    versao?: VersaoBcf
    referencialModelo?: string | null
    agora?: string
  },
): Promise<ClashBcfExportResult> {
  if (results.length === 0) throw new Error('A regra não apanha nenhum conflito desta corrida.')
  const guids = await resolveClashGuids(results)
  for (const r of results) {
    const g = opts.triage[clashKey(r)]?.guids
    if (g) guids[clashKey(r)] = [g[0], g[1]]
  }
  const nomes = await resolveClashNomes(results)
  const draft = rascunhoDoModelador(regra, results, {
    guids,
    nomes,
    triage: opts.triage,
    especialidadeDe: opts.especialidadeDe,
  })
  const refs = modelRefs()
  const viewpoint = clashViewpoint(results, {
    guidsA: results.map((r) => guids[clashKey(r)]?.[0] ?? null),
    guidsB: results.map((r) => guids[clashKey(r)]?.[1] ?? null),
    modelRefs: refs,
    semCorte: true,
  })
  const referencial = escolherReferencial(modelosGeoDaCena(), opts.referencialModelo ?? null).referencial
  const name = `Corrigir na origem: ${regra.nome}`
  const author = opts.author?.trim() || AUTOR_SEM_SESSAO
  const nowIso = opts.agora ?? new Date().toISOString()
  const prep: TopicosPreparados = {
    itens: [{ key: draft.key, results: [...results], group: null }],
    drafts: [draft],
    viewpoints: [viewpoint],
    imagens: [null],
    msPorImagem: 0,
    refs,
    referencial,
  }
  if (opts.bcfModuleEnabled) {
    const bcf = useAppStore.getState().bcfCreateEmpty(name)
    await criarNoModulo(bcf.localId, prep, {
      guids: [crypto.randomUUID()],
      relacionados: [[]],
      triage: opts.triage,
      author,
      nowIso,
      folder: name,
    })
    return { destination: 'modulo', topics: 1, name: bcf.name, imagens: 0, msPorImagem: 0 }
  }
  const { parsed, attachments } = montarBcfDosConflitos([draft], [viewpoint], [null], {
    name,
    author,
    nowIso,
    referencial,
    projecto: opts.projecto ?? null,
  })
  const blob = await buildBcfZip(parsed, attachments, {
    versao: opts.versao ?? '2.1',
    utilizadores: utilizadoresDosTopicos([draft]),
  })
  const filename = clashBcfFileName().replace(/^conflitos_/, `corrigir_na_origem_${regra.id}_`)
  downloadBlob(blob, filename, 'application/zip')
  return { destination: 'ficheiro', topics: 1, name: filename, imagens: 0, msPorImagem: 0 }
}

export interface AcrescentarAoModuloResult {
  /** O envio com os topicos novos registados. */
  envio: ClashEnvioModulo
  topicos: number
  /** Topicos existentes que ganharam a ligacao de volta. */
  ligadosDeVolta: number
  /** Topicos de grupo que receberam o comentario dos novos. */
  comentados: number
  imagens: number
  msPorImagem: number
}

/**
 * "Acrescentar os novos ao BCF": cria no BCF do envio um topico por
 * conflito novo, como na exportacao (titulo, descricao, prioridade,
 * responsavel, etiquetas, viewpoint, imagem e ancora), ligado aos topicos
 * do mesmo grupo que ja la estao (ver clashModuloBcf.ts). Devolve o envio
 * actualizado; quem chama grava-o.
 */
export async function acrescentarNovosAoModulo(
  results: readonly ClashResult[],
  envio: ClashEnvioModulo,
  opts: Omit<ClashBcfExportOptions, 'bcfModuleEnabled' | 'umPorGrupo'>,
): Promise<AcrescentarAoModuloResult> {
  if (results.length === 0) throw new Error('Sem conflitos novos para acrescentar.')
  const bcfDoEnvio = () => useAppStore.getState().bcfLoaded.find((b) => b.localId === envio.bcfLocalId) ?? null
  const bcf = bcfDoEnvio()
  if (!bcf) throw new Error('O BCF enviado já não está aberto no módulo.')
  const triage = opts.triage ?? {}
  const prep = await prepararTopicos(results, { ...opts, bcfModuleEnabled: true, umPorGrupo: false })
  const author = opts.author?.trim() || AUTOR_SEM_SESSAO
  const nowIso = opts.agora ?? new Date().toISOString()
  const existentes = new Set(latestParsed(bcf).topics.map((t) => t.guid))
  const novos: NovoTopico[] = prep.drafts.map((d) => ({
    key: d.key,
    guid: crypto.randomUUID(),
    grupo: d.grupo,
    titulo: d.title,
  }))
  const rel = relacoesDosNovos(novos, envio, triage, existentes)
  const criados = await criarNoModulo(envio.bcfLocalId, prep, {
    guids: novos.map((n) => n.guid),
    relacionados: rel.relacionados,
    triage,
    author,
    nowIso,
    folder: bcf.name,
  })
  const feitos = new Set(criados.map((c) => c.guid))
  // A ligacao de volta: os topicos do grupo que ja estavam no BCF ganham
  // os novos nos RelatedTopics (a sincronizacao junta-os a tres).
  let ligadosDeVolta = 0
  for (const [guid, acrescentar] of rel.deVolta) {
    const novosFeitos = acrescentar.filter((g) => feitos.has(g))
    const agora = bcfDoEnvio()
    const topico = agora ? latestParsed(agora).topics.find((t) => t.guid === guid) : undefined
    if (novosFeitos.length === 0 || !topico) continue
    useAppStore
      .getState()
      .bcfUpdateTopic(
        envio.bcfLocalId,
        guid,
        { relatedTopics: [...new Set([...(topico.relatedTopics ?? []), ...novosFeitos])] },
        author,
      )
    ligadosDeVolta += 1
  }
  let comentados = 0
  for (const [guid, titulos] of rel.comentarios) {
    useAppStore.getState().bcfAddComment(envio.bcfLocalId, guid, comentarioDosNovosNoGrupo(titulos, nowIso), author)
    comentados += 1
  }
  return {
    envio: envioComNovos(envio, criados, nowIso),
    topicos: criados.length,
    ligadosDeVolta,
    comentados,
    imagens: prep.imagens.filter((x) => x !== null).length,
    msPorImagem: prep.msPorImagem,
  }
}

/** O `ParsedBcf` e os anexos dos topicos de conflitos. Puro (fora os
 *  GUIDs aleatorios): e o que os testes de ida e volta exportam. */
export function montarBcfDosConflitos(
  drafts: ReturnType<typeof buildItemDrafts>,
  viewpoints: readonly Viewpoint[],
  imagens: readonly (Uint8Array | null)[],
  meta: {
    name: string
    author: string | null
    nowIso: string
    referencial?: AttachedViewpoint['referencial']
    /** Identificador do projecto (o da sessao). Com ele, os Guids do
     *  topico, do viewpoint e dos comentarios sao estaveis entre
     *  exportacoes (ver clashIds.ts); sem ele, aleatorios. */
    projecto?: string | null
    /** Exportacao parcial ("so o que mudou"): o registo da anterior e a
     *  triagem, para o comentario da mudanca de estado e os RelatedTopics
     *  para os topicos do mesmo grupo que nao vao neste ficheiro. */
    anterior?: ClashUltimaExportacao | null
    triage?: ClashTriageMap
  },
): { parsed: ParsedBcf; attachments: Record<string, AttachedViewpoint[]> } {
  const topics: BcfTopic[] = []
  const attachments: Record<string, AttachedViewpoint[]> = {}
  const projecto = meta.projecto ?? null
  const autor = meta.author?.trim() || AUTOR_SEM_SESSAO
  const guids = drafts.map((d) => (projecto ? guidDoTopico(projecto, d.identidade) : crypto.randomUUID()))
  // Os outros topicos do mesmo grupo nesta exportacao (RelatedTopic) e,
  // num ficheiro parcial, os do mesmo grupo das exportacoes anteriores.
  const anterior = meta.anterior ?? null
  const destes = relacionadosDosRascunhos(drafts, guids)
  const deAntes = anterior ? relacionadosAnteriores(drafts, guids, anterior) : drafts.map(() => [])
  const relacionados = destes.map((l, i) => [...new Set([...l, ...deAntes[i]])])
  // Num ficheiro parcial, um alterado leva o comentario da mudanca.
  const mudancas = drafts.map((d) =>
    anterior ? comentarioDaMudanca(d.keys, meta.triage ?? {}, anterior) : null,
  )
  drafts.forEach((draft, i) => {
    const guid = guids[i]
    const vpGuid = projecto ? guidDoViewpoint(projecto, draft.identidade) : crypto.randomUUID()
    topics.push({
      guid,
      index: i + 1,
      title: draft.title,
      type: draft.type,
      status: draft.status,
      priority: draft.priority,
      assignedTo: draft.assignedTo,
      description: draft.description,
      // O CreationAuthor e obrigatorio no esquema (2.1 e 3.0): sem ele o
      // IfcOpenShell e outras ferramentas recusam o topico. Em modo local
      // nao ha utilizador; fica o nome da aplicacao.
      creationAuthor: autor,
      creationDate: meta.nowIso,
      modifiedAuthor: null,
      modifiedDate: null,
      dueDate: draft.dueDate,
      stage: null,
      labels: draft.labels,
      // Um comentario por evento das corridas ("Deixou de ocorrer...",
      // "Reapareceu..."), com Guid estavel: voltar a exportar nao o
      // duplica noutra plataforma.
      comments: [
        ...draft.comentarios,
        ...(mudancas[i] ? [{ data: meta.nowIso, texto: mudancas[i]!, autor: null }] : []),
      ].map((c) => ({
        guid: projecto ? guidDoComentario(projecto, draft.identidade, c.data, c.texto) : crypto.randomUUID(),
        date: c.data,
        author: c.autor?.trim() || autor,
        comment: c.texto,
        modifiedDate: null,
        modifiedAuthor: null,
        status: null,
      })),
      viewpointCount: 1,
      viewpointRefs: [vpGuid],
      watchers: [],
      ...(relacionados[i].length > 0 ? { relatedTopics: relacionados[i] } : {}),
    })
    attachments[guid] = [
      {
        guid: vpGuid,
        viewpoint: viewpoints[i],
        snapshot: imagens[i] ?? null,
        // JPEG a 0,85: um PNG por tópico dava 157 MB em 138 tópicos.
        snapshotExt: 'jpg',
        // A câmara do conflito está na cena (Y para cima); o builder
        // converte-a para coordenadas IFC com este referencial.
        referencial: meta.referencial ?? null,
      },
    ]
  })
  return {
    parsed: {
      version: '2.1',
      sourceProjectId: projecto ?? crypto.randomUUID(),
      sourceProjectName: meta.name,
      topics,
      extensions: extensoesDosConflitos(drafts),
      warnings: [],
    },
    attachments,
  }
}
